import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

export function EnquiryDialog({ trigger, title = "Get a Free Quote", presetPackage = "", presetType = "General" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", travelers: "", travel_date: "",
    package: presetPackage, travel_type: presetType, message: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! Our travel expert will reach out shortly.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", travelers: "", travel_date: "", package: presetPackage, travel_type: presetType, message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const field = "w-full bg-transparent border-0 border-b border-ink/20 rounded-none px-0 py-3 text-ink placeholder:text-ink/40 focus-visible:ring-0 focus-visible:border-emerald focus:outline-none";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild data-testid="enquiry-dialog-trigger">{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg bg-cream border-emerald/10 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light text-emerald">{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-ink/60 -mt-1">Share a few details and we'll craft your Kerala journey — no obligation.</p>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="enquiry-form">
          <Input required placeholder="Full name" value={form.name} onChange={set("name")} className={field} data-testid="enquiry-name" />
          <div className="grid grid-cols-2 gap-4">
            <Input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="enquiry-email" />
            <Input required placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={field} data-testid="enquiry-phone" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input placeholder="Travellers (e.g. 2 adults)" value={form.travelers} onChange={set("travelers")} className={field} data-testid="enquiry-travelers" />
            <Input placeholder="Travel date" value={form.travel_date} onChange={set("travel_date")} className={field} data-testid="enquiry-date" />
          </div>
          <Input placeholder="Package of interest" value={form.package} onChange={set("package")} className={field} data-testid="enquiry-package" />
          <Textarea placeholder="Tell us about your dream trip..." value={form.message} onChange={set("message")} className={field + " min-h-[80px]"} data-testid="enquiry-message" />
          <button type="submit" disabled={loading} className="w-full bg-emerald text-cream py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-light transition-colors flex items-center justify-center gap-2" data-testid="enquiry-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Sending..." : "Send Enquiry"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
